// buildJourneyEvents — pure assembly of Journey_Events for the
// Vertical_Journey_Timeline shell.
//
// Walks every leg of `trip.flights` and emits a Stage_Event per entry returned
// by `computeStageTimeline`. Walks `trip.hotels`, `trip.carRentals`, and
// `trip.meetings` and emits Non_Stage_Events for their check-in/out, pickup/
// return, and start/end timestamps. Sorts ascending by `scheduledStart` with
// the documented tiebreakers (Stage_Events before Non_Stage_Events, then
// `legIndex` ascending with null treated as Infinity, then stable title sort).
//
// Per-leg stage resolution is wrapped in try/catch: a failing leg contributes
// no Stage_Events and an entry to `errors`, but never throws. Missing hotel /
// meeting / car-rental dates are silently skipped (errors are reserved for
// unexpected failures inside leg processing).
//
// A synthetic "Trip complete" summary event is appended when the final leg's
// resolved stage is `TRIP_COMPLETE` and no remaining events are still
// upcoming, satisfying Requirement 8.5.
//
// Validates: Requirements 2.1, 2.2, 2.4, 8.1, 8.3, 8.5

import {
  Building,
  Calendar,
  Car,
  CheckCircle,
  Clock,
  Coffee,
  MapPin,
  Navigation,
  Plane,
  PlaneLanding,
  Shield,
  Utensils,
} from 'lucide-react';
import {
  STAGES,
  STAGE_LABELS,
  computeStageTimeline,
  resolveCurrentStage,
} from '../../services/journeyStageEngine';
import { JOURNEY_STAGE_ORDER } from '../../types/tripcard';

// ── JSDoc type extensions for TripCard refactor ─────────────
/**
 * @typedef {import('../../types/tripcard').AlertInfo} AlertInfo
 * @typedef {import('../../types/tripcard').JourneyStage} JourneyStage
 */

/**
 * @typedef {Object} ClassifiedEvent
 * @property {string} id
 * @property {'stage' | 'hotel' | 'carRental' | 'meeting' | 'dining'} type
 * @property {string} [stage]
 * @property {'past' | 'current' | 'next' | 'future'} [eventState]
 * @property {Date} scheduledStart
 * @property {Date|null} scheduledEnd
 * @property {string} title
 * @property {string} [subtitle]
 * @property {import('react').ComponentType} icon
 * @property {number|null} legIndex
 * @property {JourneyStage} [parentStage] - Which stage this event belongs to (added for tree-view grouping)
 * @property {AlertInfo} [alert] - Alert condition attached to this event (cancellation, delay, gate change)
 */

// ── Icon maps ───────────────────────────────────────────────
// Mirrors the STAGE_ICONS map in JourneyOrchestrator.js so both surfaces draw
// from the same visual vocabulary. When STAGE_ICONS is promoted to the stage
// engine itself (per Requirement 9.2), this map becomes a re-export.
const STAGE_ICONS = {
  [STAGES.PRE_TRIP]: Clock,
  [STAGES.DEPARTURE_FROM_ORIGIN]: Car,
  [STAGES.AIRPORT_ENTRY]: Building,
  [STAGES.SECURITY_CHECKIN]: Shield,
  [STAGES.NAVIGATE_TO_GATE]: Navigation,
  [STAGES.WAITING_BOARDING]: Coffee,
  [STAGES.IN_FLIGHT]: Plane,
  [STAGES.ARRIVAL_NAVIGATION]: PlaneLanding,
  [STAGES.GROUND_TRANSPORT]: Car,
  [STAGES.AT_DESTINATION]: MapPin,
  [STAGES.TRIP_COMPLETE]: CheckCircle,
};

const NON_STAGE_ICONS = {
  hotel: Building,
  carRental: Car,
  meeting: Calendar,
  dining: Utensils,
};

// ── Constants ───────────────────────────────────────────────
const MINUTE_MS = 60 * 1000;
const NON_STAGE_BLOCK_MS = 60 * MINUTE_MS; // design: hotel / rental +60 min
const MEETING_FALLBACK_MS = 15 * MINUTE_MS; // design: meeting +15 min when no end

// ── Helpers ─────────────────────────────────────────────────

function toDate(value) {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function meetingStartDate(meeting) {
  // Support both explicit start timestamps and the date+time field pair that
  // the trip data currently uses.
  const direct = toDate(meeting.startDate);
  if (direct) return direct;
  if (meeting.date && meeting.time) {
    return toDate(`${meeting.date}T${meeting.time}:00`);
  }
  if (meeting.date) return toDate(meeting.date);
  return null;
}

function meetingEndDate(meeting, start) {
  const direct = toDate(meeting.endDate);
  if (direct) return direct;
  return new Date(start.getTime() + MEETING_FALLBACK_MS);
}

function flightKey(flight) {
  return flight?.flightId || flight?.flightNumber || null;
}

// ── Per-source builders ─────────────────────────────────────

function buildStageEventsForLeg(legIndex, flight, flightStatus, departureCalc, journeyData, now) {
  const stageResult = resolveCurrentStage({
    flight,
    flightStatus,
    departureCalc,
    journeyData,
    now,
  });
  const timeline = computeStageTimeline(flight, flightStatus, departureCalc);

  const events = timeline.map(({ stage, start, end }) => ({
    id: `stage:leg${legIndex}:${stage}`,
    type: 'stage',
    stage,
    scheduledStart: start,
    scheduledEnd: end,
    legIndex,
    sortOrder: 0,
    title: STAGE_LABELS[stage],
    icon: STAGE_ICONS[stage] || Clock,
    parentStage: stage,
    alert: undefined,
    stageInputs: {
      flight,
      flightStatus,
      departureCalc,
      meta: stageResult.meta,
    },
  }));

  return { events, stageResult };
}

function buildHotelEvents(hotels) {
  if (!Array.isArray(hotels)) return [];
  const events = [];

  hotels.forEach((hotel, index) => {
    const idBase = hotel.hotelId ?? hotel.id ?? `idx${index}`;
    const name = hotel.name || 'Hotel';
    const checkIn = toDate(hotel.checkInDate ?? hotel.checkIn);
    const checkOut = toDate(hotel.checkOutDate ?? hotel.checkOut);

    if (checkIn) {
      events.push({
        id: `hotel:${idBase}:in`,
        type: 'hotel',
        scheduledStart: checkIn,
        scheduledEnd: new Date(checkIn.getTime() + NON_STAGE_BLOCK_MS),
        legIndex: null,
        sortOrder: 1,
        title: 'Hotel Check-in',
        subtitle: name,
        icon: NON_STAGE_ICONS.hotel,
        parentStage: undefined,
        alert: undefined,
        sourceEvent: hotel,
        sourceIndex: index,
      });
    }

    if (checkOut) {
      events.push({
        id: `hotel:${idBase}:out`,
        type: 'hotel',
        scheduledStart: checkOut,
        scheduledEnd: new Date(checkOut.getTime() + NON_STAGE_BLOCK_MS),
        legIndex: null,
        sortOrder: 1,
        title: 'Hotel Check-out',
        subtitle: name,
        icon: NON_STAGE_ICONS.hotel,
        parentStage: undefined,
        alert: undefined,
        sourceEvent: hotel,
        sourceIndex: index,
      });
    }
  });

  return events;
}

function buildCarRentalEvents(carRentals) {
  if (!Array.isArray(carRentals)) return [];
  const events = [];

  carRentals.forEach((rental, index) => {
    const idBase = rental.rentalId ?? rental.id ?? `idx${index}`;
    const name = rental.company || 'Car Rental';
    const pickup = toDate(rental.pickupDate);
    const returned = toDate(rental.returnDate);

    if (pickup) {
      events.push({
        id: `carRental:${idBase}:pickup`,
        type: 'carRental',
        scheduledStart: pickup,
        scheduledEnd: new Date(pickup.getTime() + NON_STAGE_BLOCK_MS),
        legIndex: null,
        sortOrder: 1,
        title: 'Car Rental Pickup',
        subtitle: name,
        icon: NON_STAGE_ICONS.carRental,
        parentStage: undefined,
        alert: undefined,
        sourceEvent: rental,
        sourceIndex: index,
      });
    }

    if (returned) {
      events.push({
        id: `carRental:${idBase}:return`,
        type: 'carRental',
        scheduledStart: returned,
        scheduledEnd: new Date(returned.getTime() + NON_STAGE_BLOCK_MS),
        legIndex: null,
        sortOrder: 1,
        title: 'Car Rental Return',
        subtitle: name,
        icon: NON_STAGE_ICONS.carRental,
        parentStage: undefined,
        alert: undefined,
        sourceEvent: rental,
        sourceIndex: index,
      });
    }
  });

  return events;
}

function buildMeetingEvents(meetings) {
  if (!Array.isArray(meetings)) return [];
  const events = [];

  meetings.forEach((meeting, index) => {
    const start = meetingStartDate(meeting);
    if (!start) return;

    const idBase = meeting.id ?? `idx${index}`;
    const title = meeting.meetingTitle || meeting.title || 'Meeting';
    const subtitle = meeting.companyName || undefined;
    const end = meetingEndDate(meeting, start);

    events.push({
      id: `meeting:${idBase}`,
      type: 'meeting',
      scheduledStart: start,
      scheduledEnd: end,
      legIndex: null,
      sortOrder: 1,
      title,
      subtitle,
      icon: NON_STAGE_ICONS.meeting,
      parentStage: undefined,
      alert: undefined,
      sourceEvent: meeting,
      sourceIndex: index,
    });
  });

  return events;
}

function buildDiningEvents(diningReservations) {
  if (!Array.isArray(diningReservations)) return [];
  const events = [];

  diningReservations.forEach((reservation, index) => {
    const idBase = reservation.reservationId ?? reservation.id ?? `idx${index}`;
    const name = reservation.name || 'Dining Reservation';
    const reservationTime = toDate(reservation.reservationTime);

    // Skip reservations with missing/invalid time (treated as unscheduled downstream)
    if (!reservationTime) return;

    events.push({
      id: `dining:${idBase}`,
      type: 'dining',
      scheduledStart: reservationTime,
      scheduledEnd: new Date(reservationTime.getTime() + NON_STAGE_BLOCK_MS),
      legIndex: null,
      sortOrder: 1,
      title: 'Dining Reservation',
      subtitle: name,
      icon: NON_STAGE_ICONS.dining,
      parentStage: undefined,
      alert: undefined,
      sourceEvent: reservation,
      sourceIndex: index,
    });
  });

  return events;
}

// ── Sorting ─────────────────────────────────────────────────

function compareEvents(a, b) {
  const aMs = a.scheduledStart.getTime();
  const bMs = b.scheduledStart.getTime();
  if (aMs !== bMs) return aMs - bMs;

  // Tiebreaker 1: Stage_Events (sortOrder 0) before Non_Stage_Events (1).
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;

  // Tiebreaker 2: legIndex ascending, with null → Infinity.
  const aLeg = a.legIndex == null ? Infinity : a.legIndex;
  const bLeg = b.legIndex == null ? Infinity : b.legIndex;
  if (aLeg !== bLeg) return aLeg - bLeg;

  // Tiebreaker 3: stable sort by title.
  return (a.title || '').localeCompare(b.title || '');
}

// ── Main ────────────────────────────────────────────────────

/**
 * Assemble the flat, sorted list of `Journey_Events` for a trip.
 *
 * @param {Object} trip  Full trip object (flights, hotels, carRentals, meetings).
 * @param {Date}   now   Current clock value; used for stage resolution and the
 *                       terminal Trip_Complete synthesis.
 * @param {Object} [ctx] Optional maps keyed by flightId / legIndex. All maps
 *                       default to empty.
 * @param {Object} [ctx.flightStatuses]        { [flightId]: flightStatus }
 * @param {Object} [ctx.departureCalculations] { [legIndex]: departureCalc }
 * @param {Object} [ctx.journeyDataMap]        { [flightId]: journeyData }
 * @returns {{ events: Array<Object>, errors: Array<{ legIndex: number, message: string }> }}
 */
export function buildJourneyEvents(trip, now, ctx = {}) {
  const events = [];
  const errors = [];

  const safeNow =
    now instanceof Date && !Number.isNaN(now.getTime())
      ? now
      : new Date();

  const {
    flightStatuses = {},
    departureCalculations = {},
    journeyDataMap = {},
  } = ctx || {};

  const flights = Array.isArray(trip?.flights) ? trip.flights : [];

  // 1. Stage events, leg by leg. Each leg's stage resolution is isolated so a
  //    single bad leg cannot sink the rest of the timeline.
  let lastLegResult = null;

  flights.forEach((flight, legIndex) => {
    try {
      const key = flightKey(flight);
      const flightStatus = key != null ? flightStatuses[key] ?? null : null;
      const departureCalc = departureCalculations[legIndex] ?? null;
      const journeyData = key != null ? journeyDataMap[key] ?? null : null;

      const { events: legEvents, stageResult } = buildStageEventsForLeg(
        legIndex,
        flight,
        flightStatus,
        departureCalc,
        journeyData,
        safeNow,
      );

      events.push(...legEvents);
      lastLegResult = { legIndex, flight, flightStatus, departureCalc, stageResult };
    } catch (err) {
      errors.push({
        legIndex,
        message: err && err.message ? err.message : String(err),
      });
    }
  });

  // 2. Non-stage events. Missing dates are silently skipped (the design
  //    reserves the errors array for unexpected failures inside leg
  //    processing).
  events.push(...buildHotelEvents(trip?.hotels));
  events.push(...buildCarRentalEvents(trip?.carRentals));
  events.push(...buildMeetingEvents(trip?.meetings));
  events.push(...buildDiningEvents(trip?.diningReservations));

  // 3. Sort ascending by scheduledStart, applying the documented tiebreakers.
  events.sort(compareEvents);

  // 4. Synthesize the terminal "Trip complete" summary event when the final
  //    leg's resolved stage is TRIP_COMPLETE and nothing remains ahead. This
  //    keeps the timeline's last row meaningful once the engine reports the
  //    trip is done.
  if (lastLegResult && lastLegResult.stageResult.stage === STAGES.TRIP_COMPLETE) {
    const nowMs = safeNow.getTime();
    const hasUpcomingEvent = events.some((e) => {
      const endMs = e.scheduledEnd ? e.scheduledEnd.getTime() : null;
      return endMs != null && endMs > nowMs;
    });

    if (!hasUpcomingEvent) {
      const maxEndMs = events.reduce((max, e) => {
        const endMs = e.scheduledEnd ? e.scheduledEnd.getTime() : 0;
        return endMs > max ? endMs : max;
      }, 0);
      const terminalStart = maxEndMs > 0 ? new Date(maxEndMs) : safeNow;

      events.push({
        id: `stage:leg${lastLegResult.legIndex}:${STAGES.TRIP_COMPLETE}`,
        type: 'stage',
        stage: STAGES.TRIP_COMPLETE,
        scheduledStart: terminalStart,
        scheduledEnd: terminalStart,
        legIndex: lastLegResult.legIndex,
        sortOrder: 0,
        title: STAGE_LABELS[STAGES.TRIP_COMPLETE],
        icon: STAGE_ICONS[STAGES.TRIP_COMPLETE],
        parentStage: STAGES.TRIP_COMPLETE,
        alert: undefined,
        stageInputs: {
          flight: lastLegResult.flight,
          flightStatus: lastLegResult.flightStatus,
          departureCalc: lastLegResult.departureCalc,
          meta: lastLegResult.stageResult.meta,
        },
      });
    }
  }

  return { events, errors };
}

/**
 * Returns the appropriate JOURNEY_STAGE_ORDER for a trip.
 * Appends 'at_destination' only when the trip has dining reservations,
 * so flight-only trips don't get an extra stage node.
 *
 * @param {Object} trip - The trip object
 * @returns {Array<string>} The stage order to use with classifyStageNodes
 */
export function getJourneyStageOrder(trip) {
  const hasDining = Array.isArray(trip?.diningReservations) && trip.diningReservations.length > 0;
  if (hasDining) {
    return [...JOURNEY_STAGE_ORDER, 'at_destination'];
  }
  return JOURNEY_STAGE_ORDER;
}

export default buildJourneyEvents;
